import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { errorToast } from 'basics/utils/toast';
import { RegisterParticipantInput } from 'generated/event.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export type UseAddParticipantMutationResponse = {
  registerParticipant: boolean;
};

export type UseAddParticipantMutation = [
  (participant: RegisterParticipantInput) => Promise<boolean>,
  MutationResult<UseAddParticipantMutationResponse>,
];

export const addParticipantMutationCall = gql`
mutation registerParticipant($participant: RegisterParticipantInput!) {
  registerParticipant(participant: $participant)
}`;

export const useAddParticipantMutation = (): UseAddParticipantMutation => {
  const { t } = useTranslation();
  const [registerInviteeMutation, state] = useMutation<UseAddParticipantMutationResponse>(addParticipantMutationCall);

  const executeMutation = useCallback(async (participant) => {
    try {
      const { data } = await registerInviteeMutation({
        variables: { participant },
      });
      if (!data?.registerParticipant) {
        errorToast(t('errors_standard'));
      }
      return !!data?.registerParticipant;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'addParticipant.ts - useAddParticipantMutation',
        });
        errorToast(t('errors_standard'));
      }
      return false;
    }
  }, [registerInviteeMutation, t]);

  return [executeMutation, state];
};
